// src/components/dartboard/DartboardCanvas.jsx
import React, { useRef, useEffect } from 'react';
import { FaDownload, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const DartboardCanvas = ({ questions = [], responses = [], readonly = false, eventName = 'dartboard' }) => {
  const canvasRef = useRef(null);

  const exportToJPEG = () => {
    const canvas = canvasRef.current;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    
    // Set flag for export (to add white background)
    tempCanvas.dataset.forExport = 'true';
    
    const tempContext = tempCanvas.getContext('2d');
    drawDartboard(tempContext);
    
    const link = document.createElement('a');
    link.download = `${eventName}-results.jpg`;
    link.href = tempCanvas.toDataURL('image/jpeg', 1.0);
    link.click();
};


  const exportToExcel = () => {
    if (!responses.length) {
      alert('No responses to export');
      return;
    }

    const data = responses.map((response, idx) => ({
      'User': Math.floor(idx / questions.length) + 1,
      'Question': questions[response.questionIndex],
      'Score': response.score,
      'Timestamp': new Date(response.timestamp).toLocaleString()
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Responses");
 XLSX.writeFile(wb, `${eventName.replace(' ', '_')}-responses.xlsx`);
  };

  const drawDartboard = (ctx) => {
    const centerX = ctx.canvas.width / 2;
    const centerY = ctx.canvas.height / 2;
    
    // Clear canvas
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // If background color is provided (for export), fill it
    if (ctx.canvas.dataset.forExport) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }
    
    // Draw circles
    const colors = ['#FFE4E1', '#FFD4D1', '#FFC4C1', '#FFB4B1', '#FFA4A1'];
    const radiuses = [200, 160, 120, 80, 40];
    
    radiuses.forEach((radius, index) => {
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = colors[index];
      ctx.fill();
      ctx.strokeStyle = '#999';
      ctx.stroke();
    });

    // Draw axes only if there's more than one question
    const numQuestions = questions.length;
    if (numQuestions > 1) {
      const angleStep = (2 * Math.PI) / numQuestions;
      
      // Draw axes
      for (let i = 0; i < numQuestions; i++) {
        const angle = i * angleStep;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(
          centerX + Math.cos(angle) * radiuses[0],
          centerY + Math.sin(angle) * radiuses[0]
        );
        ctx.strokeStyle = '#999';
        ctx.stroke();
      }

      // Configure text style
      ctx.font = '14px Arial';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Position and draw text for each question
      questions.forEach((question, i) => {
        const angle = i * angleStep + (angleStep / 2);
        const textRadius = radiuses[0] + 100; // Increased distance from dartboard
        const textX = centerX + Math.cos(angle) * textRadius;
        const textY = centerY + Math.sin(angle) * textRadius;

        // Calculate available width for text
        const maxWidth = radiuses[0] * 0.8; // 80% of the dartboard radius
        const words = question.split(' ');
        const lines = [];
        let currentLine = words[0];

        // Break text into lines
        for (let i = 1; i < words.length; i++) {
          const testLine = currentLine + ' ' + words[i];
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth) {
            lines.push(currentLine);
            currentLine = words[i];
          } else {
            currentLine = testLine;
          }
        }
        lines.push(currentLine);

        // Draw each line with proper spacing
        const lineHeight = 20;
        const totalHeight = lines.length * lineHeight;
        
        lines.forEach((line, lineIndex) => {
          const lineY = textY - (totalHeight / 2) + (lineIndex * lineHeight);
          ctx.fillText(line, textX, lineY);
        });
      });
    } else if (numQuestions === 1) {
      // Handle single question case
      ctx.font = '14px Arial';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const question = questions[0];
      const textY = centerY - radiuses[0] - 40; // Position above the dartboard

      // Calculate available width for text
      const maxWidth = radiuses[0] * 1.5;
      const words = question.split(' ');
      const lines = [];
      let currentLine = words[0];

      // Break text into lines
      for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + ' ' + words[i];
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxWidth) {
          lines.push(currentLine);
          currentLine = words[i];
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine);

      // Draw each line with proper spacing
      const lineHeight = 20;
      lines.forEach((line, lineIndex) => {
        ctx.fillText(line, centerX, textY + (lineIndex * lineHeight));
      });
    }

    // Plot responses
    const userSets = [];
    for (let i = 0; i < responses.length; i += numQuestions) {
      const set = responses.slice(i, i + numQuestions);
      if (set.length === numQuestions) userSets.push(set);
    }

    const generateUserColors = (count) => {
      const colors = [];
      for (let i = 0; i < count; i++) {
        const hue = (i * 137.508) % 360;
        colors.push(`hsl(${hue}, 70%, 45%)`);
      }
      return colors;
    };

    const userColors = generateUserColors(Math.min(100, userSets.length));
    
    userSets.forEach((userSet, userIndex) => {
      const userColor = userColors[userIndex % userColors.length];
      
      userSet.forEach(response => {
        let angle;
        if (numQuestions > 1) {
          const baseAngle = (response.questionIndex * (2 * Math.PI / numQuestions));
          // Calculate angle to favor middle of sectors
          const sectorSize = 2 * Math.PI / numQuestions;
          const midSectorAngle = baseAngle + (sectorSize / 2);
          const minOffset = -sectorSize / 3;
          const maxOffset = sectorSize / 3;
          const angleOffset = minOffset + Math.random() * (maxOffset - minOffset);
          angle = midSectorAngle + angleOffset;
        } else {
          angle = Math.random() * 2 * Math.PI;
        }
        
        // Calculate radius with better spacing
        const score = response.score;
        const circleSpacing = 40;
        const minRadius = (5 - score) * circleSpacing + 3;
        const maxRadius = (6 - score) * circleSpacing - 3;
        const radius = minRadius + Math.random() * (maxRadius - minRadius);
        
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        // Draw response dot
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = userColor;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    drawDartboard(ctx);
  }, [questions, responses]);

  return (
    <div className="dartboard-container">
      <canvas
        ref={canvasRef}
        width={800}
        height={800}
        style={{
          margin: '0px',
          borderRadius: '50%'
        }}
      />
  
      <div className="response-stats">
        <p>Total de Respostas: {Math.floor((responses?.length || 0) / questions.length)}</p>
      </div>
      
      <div className="export-buttons">
        <button 
          onClick={exportToJPEG}
          className="export-button"
          title="Export as JPEG"
        >
          <FaDownload /> Export Image
        </button>
        <button 
          onClick={exportToExcel}
          className="export-button"
          title="Export as Excel"
        >
          <FaFileExcel /> Export Data
        </button>
      </div>
    </div>
  );
  
};

export default DartboardCanvas;
